/*
  Collection grid / list view toggle.

  The chosen view is a URL parameter, ?display=list, not browser storage. That
  makes it linkable, shareable and reproducible, and it stops a choice made on
  one device following the visitor to another.

  Switching does not reload the page. The class is applied, the URL is
  rewritten with replaceState, and the hidden inputs described below are
  updated, so a subsequent filter or sort carries the choice forward.

  The class goes on <html>, not on the grid, for two reasons: the inline script
  in the snippet can set it during parse before anything paints, and Dawn
  replaces the whole #product-grid element on every filter and sort, which
  would take a class set on the grid with it.
*/
class CollectionViewToggle extends HTMLElement {
  connectedCallback() {
    this.param = this.dataset.param || 'display';
    this.buttons = Array.from(this.querySelectorAll('[data-view]'));

    this.syncFromUrl();
    this.buttons.forEach((button) => {
      button.addEventListener('click', () => this.select(button.dataset.view));
    });

    /*
      Two different mechanisms, because filtering and paginating lose the
      parameter in two different ways.

      Filter and sort: Dawn serialises the facet forms into the query string
      and drops anything not in a form, so the view rides along as a hidden
      input. Selected by `facet-filters-form form` rather than by the id values
      Dawn happens to use today, so a form it adds later still works.

      Pagination: the links are built server side from paginate.parts, and
      those preserve only the parameters Shopify recognises - sort_by, filter.*
      and q. An arbitrary one like this is silently dropped, so the hrefs are
      rewritten here instead.
    */
    this.syncForms();
    this.syncLinks();

    /*
      Dawn replaces the whole inner HTML of #ProductGridContainer on every
      filter and sort, which throws away the rewritten pagination links along
      with the old grid. It publishes no event for that, so the container is
      observed directly.
    */
    const container = document.getElementById('ProductGridContainer');
    if (container) {
      this.observer = new MutationObserver(() => this.syncLinks());
      this.observer.observe(container, { childList: true, subtree: true });
    }

    document.addEventListener('shopify:section:load', () => {
      this.syncForms();
      this.syncLinks();
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  get current() {
    return new URLSearchParams(window.location.search).get(this.param) === 'list' ? 'list' : 'grid';
  }

  select(view) {
    const url = new URL(window.location.href);
    if (view === 'list') {
      url.searchParams.set(this.param, 'list');
    } else {
      url.searchParams.delete(this.param);
    }
    window.history.replaceState({}, '', url);

    this.apply(view);
    this.syncForms();
    this.syncLinks();
  }

  syncFromUrl() {
    this.apply(this.current);
  }

  apply(view) {
    document.documentElement.classList.toggle('collection-view-list', view === 'list');
    this.buttons.forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.view === view ? 'true' : 'false');
    });
  }

  syncForms() {
    const view = this.current;
    document.querySelectorAll('facet-filters-form form').forEach((form) => {
      let input = form.querySelector(`input[name="${this.param}"]`);
      if (view !== 'list') {
        if (input) input.remove();
        return;
      }
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = this.param;
        form.appendChild(input);
      }
      input.value = 'list';
    });
  }

  syncLinks() {
    /*
      Guarded against re-entry: rewriting an href inside the observer's own
      subtree fires the observer again, and without this the two would feed
      each other indefinitely.
    */
    if (this.syncingLinks) return;
    this.syncingLinks = true;

    const view = this.current;
    document.querySelectorAll('.pagination__item[href]').forEach((link) => {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      if (view === 'list') {
        url.searchParams.set(this.param, 'list');
      } else {
        url.searchParams.delete(this.param);
      }
      link.setAttribute('href', `${url.pathname}${url.search}`);
    });

    this.syncingLinks = false;
  }
}

customElements.define('collection-view-toggle', CollectionViewToggle);
